import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/shop';
import { useAuth } from '../auth/AuthContext';
import { QrCode } from '../components/QrCode';
import { formatMoney } from '../brand/brand';
import type { PlaceTarget, PlaceWallet } from '../api/types';

/**
 * What you get when you scan the sticker on a counter. SOW §3, TC-02.
 *
 * ── The situation this page is for ───────────────────────────────────────────
 *
 * A small shop with no till app and no trained staff. There is a printed code by
 * the register. The customer scans it, and until now that led nowhere at all.
 *
 * They are standing at a counter with somebody waiting. So the page answers, in
 * this order: where am I, what have I got here, and what do I hold up. Everything
 * else is below the fold or absent.
 *
 * ── Signed out is a real state, not an error ─────────────────────────────────
 *
 * Anyone can scan a sticker — a passer-by, somebody with no account, somebody
 * curious what the code does. That gets the shop's name and a link to its deals,
 * which is a decent outcome. Only the wallet half needs an account.
 */
export function PlaceScan() {
  const { token } = useParams<{ token: string }>();
  const { userId, ready } = useAuth();
  const isSignedIn = userId !== null;

  const [place, setPlace] = useState<PlaceTarget | null>(null);
  const [wallet, setWallet] = useState<PlaceWallet | null>(null);
  const [failed, setFailed] = useState(false);
  const [showing, setShowing] = useState<string | null>(null);

  useEffect(() => {
    if (token === undefined) {
      setFailed(true);
      return;
    }

    // Wait for the stored session to be restored. Firing the anonymous call first
    // would flash "sign in to see your vouchers" at somebody who IS signed in —
    // at a counter, with somebody waiting.
    if (!ready) return;

    // Signed in: one call gets the place AND what they hold. Signed out: the
    // anonymous one. Never both — the second would be a guaranteed 401 in the
    // console on every scan by a signed-out visitor.
    if (isSignedIn) {
      api.placeWallet(token)
        .then((w) => { setWallet(w); setPlace(w.place); })
        .catch(() => setFailed(true));
    } else {
      api.place(token).then(setPlace).catch(() => setFailed(true));
    }
  }, [token, isSignedIn, ready]);

  if (failed) {
    return (
      <div className="page narrow stack">
        <h1>That code did not work</h1>
        <p className="muted">
          The sticker may have been replaced. Ask whoever is serving you, or find the
          business from the home page.
        </p>
        <Link className="btn" to="/">Browse deals</Link>
      </div>
    );
  }

  if (place === null) {
    return <div className="page narrow"><p className="muted">Loading…</p></div>;
  }

  return (
    <div className="page narrow stack">
      <header className="stack gap-xs">
        <p className="faint">You are at</p>
        <h1>{place.businessName}</h1>
        <p className="muted">
          {place.branchName}
          {place.addressLine1 !== null && ` · ${place.addressLine1}`}
          {place.city !== null && `, ${place.city}`}
        </p>
      </header>

      {!place.acceptingCustomers && (
        // Said plainly rather than by hiding the page. Somebody is standing in
        // front of the sticker; "not right now" is an answer, a broken link is not.
        <p className="notice warn">
          This place is not taking vouchers at the moment.
        </p>
      )}

      {!isSignedIn ? (
        <div className="stack gap-sm">
          <p className="muted">
            Sign in to see any vouchers you hold here.
          </p>
          <Link className="btn primary" to="/signin">Sign in</Link>
          <Link className="btn" to={`/?business=${place.businessSlug}`}>
            See what {place.businessName} is offering
          </Link>
        </div>
      ) : wallet === null ? (
        <p className="muted">Loading your vouchers…</p>
      ) : wallet.vouchers.length === 0 ? (
        <div className="stack gap-sm">
          <p className="muted">
            You do not have anything to spend here yet.
          </p>
          <Link className="btn primary" to={`/?business=${place.businessSlug}`}>
            See what {place.businessName} is offering
          </Link>
        </div>
      ) : (
        <>
          <p className="lead">
            You have <strong>{formatMoney(wallet.totalSpendableCents)}</strong> to spend
            here.
          </p>

          <ul className="place-vouchers">
            {wallet.vouchers.map((v) => (
              <li key={v.id}>
                <div className="row spread">
                  <div className="stack gap-xs">
                    <strong>{v.dealTitle}</strong>
                    <span className="faint">
                      {formatMoney(v.balanceCents)} left · until{' '}
                      {new Date(v.validTo).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowing(showing === v.id ? null : v.id)}
                  >
                    {showing === v.id ? 'Hide' : 'Show'}
                  </button>
                </div>

                {showing === v.id && (
                  <div className="place-show">
                    {/* Large and on white. The person reading it may be typing it
                        by hand into a card machine, or scanning it off a phone at
                        arm's length in whatever lighting the shop has. */}
                    <div className="pass-qr">
                      <QrCode value={v.code} size={200} />
                    </div>

                    <p className="pass-code" aria-label={v.code.split('').join(' ')}>
                      {v.code}
                    </p>

                    {v.pinRequired && (
                      // The PIN is NOT printed here, and this line explains why the
                      // cashier is about to ask for something the screen does not
                      // show — otherwise it reads as the app being broken.
                      <p className="pass-note">
                        They will also ask for your 4-digit PIN. It is in the voucher's
                        own page, not on this screen — so a photo of this is not enough
                        to spend it.
                      </p>
                    )}

                    <Link className="btn ghost" to={`/vouchers/${v.id}`}>
                      Open this voucher
                    </Link>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
